import { Box, Container, Divider, Grid, Link, Typography } from '@mui/material'
import { useAuth } from 'context/JWTContext'
import Script from 'next/script'
import { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import { PartnerPageData } from 'types/types'
import {
  getStarSportsFooterColor,
  getStarSportsFooterWording,
  isStarSportsPage,
} from '@/utils/starSportsUtils'
import { isTBLPage } from '@/utils/tblUtils'
import { TEXT_DARK_COLORS_VARIANTS } from '@/utils/constants/colors'
import { FooterGambleAwareLogo } from './FooterGambleAwareLogo'
import { GambleAwareBadge } from './GambleAwareBadge'
import { ContactUsDrawer } from './prize-draws/profile-drawer/ContactUsDrawer'

interface FooterProps {
  page: PartnerPageData
  marginTop?: string
}

function Footer({ page, marginTop = '30px' }: FooterProps) {
  const { isAuthenticated } = useAuth()
  const router = useRouter()
  const { slug } = router.query
  const primaryColor = '#FFC107'

  const isTBL = isTBLPage(page)

  const trustpilotRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isTBL && trustpilotRef.current) {
      const tp = (window as any).Trustpilot
      if (tp) {
        tp.loadFromElement(trustpilotRef.current, true)
      }
    }
  }, [isTBL])

  return (
    <Box marginTop={marginTop}>
      {isTBL && (
        <>
          <Script
            id="trustpilot-widget"
            src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
            strategy="afterInteractive"
            onLoad={() => {
              if (trustpilotRef.current) {
                ;(window as any).Trustpilot?.loadFromElement(trustpilotRef.current, true)
              }
            }}
          />
          <Box bgcolor={page.background_color} pt={2}>
            <Container maxWidth="md">
              <div
                ref={trustpilotRef}
                className="trustpilot-widget"
                data-locale="en-US"
                data-template-id="56278e9abfbbba0bdcd568bc"
                data-businessunit-id="691b07e6aa94cfc902515e4a"
                data-style-height="52px"
                data-style-width="100%"
                data-token="cb528f7e-a9d5-4212-a5a6-0b13aa6431f1"
                style={{ textAlign: 'center' }}
              >
                <a
                  href="https://www.trustpilot.com/review/thebigleagues.co.uk"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Trustpilot
                </a>
              </div>
            </Container>
          </Box>
        </>
      )}
      <Box bgcolor={page.background_color} py={4} textAlign="center" justifyContent="center">
        {isStarSportsPage(page) && (
          <Box maxWidth="90%" textAlign="center" margin="0 auto">
            <Divider color={TEXT_DARK_COLORS_VARIANTS[300]} sx={{ mb: 2 }} />
            <Box maxWidth="800px" textAlign="center" margin="0 auto" p={2}>
              <Typography variant="body2" color={getStarSportsFooterColor(page)} fontStyle="italic">
                18+. Active and eligible Star Sports UK & ROI customers only. Free to play. Entry
                before selected match kick-off or first race start time. Prizes awarded midday
                following day. Free Bet valid for 7 days.
              </Typography>
            </Box>
          </Box>
        )}

        <Container
          maxWidth="md"
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: isStarSportsPage(page) ? 'center' : 'space-between',
            alignItems: 'center',
            gap: 4,
            textAlign: 'center',
            flexWrap: isStarSportsPage(page) ? 'wrap' : 'nowrap',
          }}
        >
          <Box
            sx={{
              width: isStarSportsPage(page) ? '100%' : 'auto',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            {(page.game_featured_image || page.logo) && !isStarSportsPage(page) && (
              <img
                width={150}
                height={50}
                src={page.game_featured_image || page.logo}
                alt="Logo"
                style={{ width: 'auto', height: 'auto', maxWidth: '150px', maxHeight: '50px' }}
              />
            )}
          </Box>

          {isStarSportsPage(page) ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                flexWrap: 'wrap',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  width: '200px',
                }}
              >
                <FooterGambleAwareLogo page={page} />
              </Box>
              <Box
                component="ul"
                sx={{
                  m: 0,
                  p: 0,
                  listStyleType: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                <Box component="li">
                  <Link href={`/${slug}/termsandconditions`} underline="none">
                    <Typography
                      variant="body2"
                      style={{
                        color: getStarSportsFooterColor(page),
                        fontStyle: 'italic',
                      }}
                      fontFamily={page.fonts.secondary_font}
                    >
                      {getStarSportsFooterWording(page)}
                    </Typography>
                  </Link>
                </Box>
                <Box component="li">
                  <Link href={`/${slug}/privacypol`} underline="none">
                    <Typography
                      variant="body2"
                      style={{ color: getStarSportsFooterColor(page) }}
                      fontFamily={page.fonts.secondary_font}
                    >
                      Privacy Policy
                    </Typography>
                  </Link>
                </Box>
                <Box component="li">
                  <ContactUsDrawer
                    partnerId={page.id}
                    trigger={
                      <Typography
                        variant="body2"
                        style={{
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          color: getStarSportsFooterColor(page),
                          cursor: 'pointer',
                        }}
                        fontFamily={page.fonts.secondary_font}
                      >
                        Contact us
                      </Typography>
                    }
                  />
                </Box>
              </Box>
            </Box>
          ) : (
            <>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                flexDirection="column"
                gap={3}
              >
                <Grid item xs={12} sm={12} md={4} alignSelf="center">
                  <Typography variant="body2" component="div" color="black">
                    <Box component="ul" sx={{ m: 0, p: 0, listStyleType: 'none' }}>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Link href={`/${slug}/termsandconditions`} underline="none">
                          <Typography
                            variant="body2"
                            style={{
                              color: getStarSportsFooterColor(page),
                              fontStyle: isStarSportsPage(page) ? 'italic' : 'normal',
                            }}
                            fontFamily={page.fonts.secondary_font}
                          >
                            {getStarSportsFooterWording(page)}
                          </Typography>
                        </Link>
                      </Box>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Link href={`/${slug}/privacypol`} underline="none">
                          <Typography
                            variant="body2"
                            style={{ color: getStarSportsFooterColor(page) }}
                            fontFamily={page.fonts.secondary_font}
                          >
                            Privacy Policy
                          </Typography>
                        </Link>
                      </Box>
                      <ContactUsDrawer
                        partnerId={page.id}
                        trigger={
                          <Typography
                            variant="body2"
                            style={{
                              fontWeight: 'bold',
                              textDecoration: 'none',
                              color: getStarSportsFooterColor(page),
                              cursor: 'pointer',
                            }}
                            fontFamily={page.fonts.secondary_font}
                          >
                            Contact us
                          </Typography>
                        }
                      />
                    </Box>
                  </Typography>
                </Grid>

                {isTBL && (
                  <GambleAwareBadge
                    bgcolor={page.background_color}
                    color={getStarSportsFooterColor(page)}
                    fontFamily={page.fonts.secondary_font.family}
                  />
                )}

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.75,
                  }}
                >
                  <Typography
                    variant="body2"
                    style={{ color: getStarSportsFooterColor(page) }}
                    fontFamily={page.fonts.secondary_font}
                  >
                    © {new Date().getFullYear()} Hunch. All rights reserved.
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ color: getStarSportsFooterColor(page) }}
                    fontFamily={page.fonts.secondary_font}
                    sx={{ display: { xs: 'block', sm: 'none' } }}
                  >
                    Powered by <strong>Hunch</strong>
                  </Typography>
                </Box>
              </Box>

              <Typography
                variant="body2"
                style={{ color: getStarSportsFooterColor(page) }}
                fontFamily={page.fonts.secondary_font}
                sx={{ display: { xs: 'none', sm: 'block' } }}
              >
                Powered by <strong>Hunch</strong>
              </Typography>
            </>
          )}
        </Container>
      </Box>
    </Box>
  )
}

export default Footer
